import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import type { Hotkey } from '../hotkeys';
import { Key } from '../hotkeys/KeyStrokes.ts';
import { CameraControlHotkeyNames, MouseAction } from '../hotkeys/types.ts';

export type CameraControlHotKeyDefinitionsResult = ReturnType<
  typeof useCameraControlHotKeyDefinitions
>;

export const useCameraControlHotKeyDefinitions = () => {
  const { t } = useTranslation(['common', 'landingPage']);

  return useMemo(
    () => ({
      // Navigation & fullscreen (single-shot shortcuts in `useCameraControlHotkeys`)
      cameraControlHotKeys: {
        [CameraControlHotkeyNames.FocusNextVideoStream]: {
          primary: Key.arrow.ArrowRight,
          description: t('common:shortcuts.camera.moveToNextCamera'),
          hotkeyModifier: { isModifierIconLast: true, modifier: 'Alt' },
        },
        [CameraControlHotkeyNames.FocusPreviousVideoStream]: {
          primary: Key.arrow.ArrowLeft,
          description: t('common:shortcuts.camera.moveToPreviousCamera'),
          hotkeyModifier: { isModifierIconLast: true, modifier: 'Alt' },
        },
        [CameraControlHotkeyNames.ToggleFullScreen]: {
          primary: Key.navigation.Enter,
          hotkeyModifier: { isModifierIconLast: true, modifier: 'Alt' },
          description: t('common:shortcuts.camera.toggleFullScreenVideo'),
        },
        [CameraControlHotkeyNames.AlternativeToggleFullScreen]: {
          primary: Key.KeyF,
          description: t('common:shortcuts.camera.toggleFullScreenVideo'),
          mouseAction: MouseAction.DOUBLE_CLICK,
        },
        [CameraControlHotkeyNames.CycleCamera]: {
          primary: Key.KeyC,
          description: t('common:shortcuts.camera.nextCameraInGroup'),
        },
        [CameraControlHotkeyNames.SwitchCamera]: {
          primary: Key.KeyC,
          hotkeyModifier: { isModifierIconLast: false, modifier: 'Shift' },
          description: t('landingPage:switchCamera'),
          mouseAction: MouseAction.RIGHT_CLICK,
        },
      } satisfies Record<string, Hotkey>,
      // Pan/tilt (hold-repeat in camera stream widget)
      cameraControlHoldRepeatHotkeys: {
        [CameraControlHotkeyNames.TiltCameraUp]: {
          primary: Key.arrow.ArrowUp,
          optionalPrimary: Key.KeyW,
          description: t('common:shortcuts.camera.tiltCameraUp'),
        },
        [CameraControlHotkeyNames.TiltCameraDown]: {
          primary: Key.arrow.ArrowDown,
          optionalPrimary: Key.KeyS,
          description: t('common:shortcuts.camera.tiltCameraDown'),
        },
        [CameraControlHotkeyNames.PanCameraLeft]: {
          primary: Key.arrow.ArrowLeft,
          optionalPrimary: Key.KeyA,
          description: t('common:shortcuts.camera.panCameraLeft'),
        },
        [CameraControlHotkeyNames.PanCameraRight]: {
          primary: Key.arrow.ArrowRight,
          optionalPrimary: Key.KeyD,
          description: t('common:shortcuts.camera.panCameraRight'),
        },
      } as const satisfies Record<string, Hotkey>,
    }),
    [t]
  );
};
